import { Link } from "react-router-dom";
import { DataTable, DataTablePFSEvent } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";

import classes from "./lead-list.module.scss";
import { requests } from "../../../../services/http-common";
import { useQuery } from "@tanstack/react-query";
import { Actions } from "./Columns/Actions";
import { Status } from "./Columns/Status";
import { UpdatedAt } from "./Columns/UpdatedAt";
import { GeocodeStatus } from "./Columns/GeocodeStatus";
import { LeadStatusEnum } from "../Lead.model";
import { Dropdown, DropdownChangeParams } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { useCallback, useMemo, useState } from "react";
import { debounce } from "../../../../helpers/debounce";
import { Loader } from "../../../../components/Loader/Loader";
import { SectionHeader } from "../../../../components/SectionHeader/SectionHeader";
import { IFilter } from "../../../../types";
import { useFilterQuery } from "../../../../hooks/useFilterQuery";
import { IContract } from "../../Contracts/Contracts.model";
import { ImportExportData } from "../../../../components/import-export-data/import-export-data";
import { PrivateAccess } from "../../../../keycloak";
import { UserRoles } from "../../../../constants/userRoles";
import { useEffectWithoutMount } from "../../../../helpers/helperHooks";
import { LabelValue } from "../../../../components/LabelValue/LabelValue";

const statusOptions = [
  {
    label: "All",
    value: "",
  },
  {
    label: "Approved",
    value: LeadStatusEnum.Approved,
  },
  {
    label: "Denied",
    value: LeadStatusEnum.Denied,
  },
  {
    label: "Pending",
    value: LeadStatusEnum.Pending,
  },
];

const defaultFilters = {
  first: 0,
  rows: 10,
  page: 1,
  filter: "",
  filterByStatus: "",
  filterByCustomerCategory: "",
  filterByProjectType: "",
  filterByContractType: "",
  sortOrder: -1,
};

export const LeadList = () => {
  const [filters, setFilters] = useState<IFilter>(defaultFilters as IFilter);
  const [term, setTerm] = useState("");
  const filterQuery = useFilterQuery(filters);

  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: [`leads-list`],
    queryFn: () => {
      return requests.get(`/lead?${filterQuery}`);
    },
    retry: false,
    cacheTime: 0,
  });

  const { data: contractTypeOptionsData } = useQuery({
    queryKey: ["contract-type-options"],
    queryFn: () => {
      return requests.get("/contract-type?rows=1000");
    },
  });

  const projectTypeOptions = useMemo(() => {
    if (!contractTypeOptionsData) return [];

    const options = contractTypeOptionsData.result.map((option: any) => ({
      label: option.name,
      value: option.id,
      id: option.id,
    }));

    return [{ label: "All", value: "" }, ...options];
  }, [contractTypeOptionsData]);

  useEffectWithoutMount(() => {
    refetch();
  }, [filters, refetch]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedOnSearch = useCallback(
    debounce((event: any) => {
      setFilters((prev) => ({ ...prev, filter: event.target.value }));
    }, 500),
    [debounce]
  );

  const handleInputSearch = (e: any) => {
    debouncedOnSearch(e);
    setTerm(e.target.value);
  };

  const onDropdownChange = (e: DropdownChangeParams) => {
    setFilters((prev) => ({ ...prev, [e.target.id]: e.value }));
  };

  const onPage = useCallback(
    (event: DataTablePFSEvent) => {
      setFilters((currVal: any) => ({
        ...currVal,
        first: event.first,
        rows: event.rows,
        page: event.page,
      }));
    },
    [setFilters]
  );

  return (
    <div className={classes.container}>
      <SectionHeader title="Leads" />
      <PrivateAccess
        roles={{
          dcs_client: [UserRoles.LEAD_CREATE],
        }}
      >
        <Link to="create" className={classes.createLeadBtn}>
          <Button
            label="Create Lead"
            aria-label="create-lead"
            className="p-button-sm"
          />
        </Link>
      </PrivateAccess>
      <div className={classes.filters}>
        <div className={classes.filterGroup}>
          <div className={classes.field}>
            <LabelValue label="Select Project Type">
              <Dropdown
                appendTo="self"
                options={projectTypeOptions}
                id="filterByContractType"
                onChange={onDropdownChange}
                value={filters.filterByContractType}
                className={classes.dropdown}
              />
            </LabelValue>
          </div>
          <div className={classes.field}>
            <LabelValue label="Select Status">
              <Dropdown
                appendTo="self"
                options={statusOptions}
                id="filterByStatus"
                onChange={onDropdownChange}
                value={filters.filterByStatus}
                className={classes.dropdown}
              />
            </LabelValue>
          </div>
        </div>
        <div>
          <ImportExportData
            exportUrl={`/lead/export-csv?${filterQuery}`}
            importUrl="/lead/import-csv"
            refetchQury={refetch}
            viewRole={UserRoles.LEAD_VIEW}
          />

          <span className="p-input-icon-left">
            <i className="pi pi-search" />
            <InputText
              onChange={handleInputSearch}
              value={term}
              placeholder="Search"
            />
          </span>
        </div>
      </div>
      {isLoading ? (
        <Loader />
      ) : (
        <div className={classes.table}>
          <DataTable
            responsiveLayout="scroll"
            value={data?.result}
            paginator
            paginatorTemplate="PrevPageLink CurrentPageReport NextPageLink"
            first={filters.first}
            rows={filters.rows}
            totalRecords={data?.total}
            onPage={onPage}
            paginatorPosition="top"
            paginatorClassName={classes.paginator}
            tableClassName={classes.dataTable}
            lazy
          >
            <Column
              header="Lead Name"
              body={(rowData) =>
                rowData.client ? (
                  <Link
                    className={classes.leadName}
                    to={`/sales/client/${rowData.client.id}/details`}
                  >
                    {rowData.companyName
                      ? rowData.contactName
                      : `${rowData.name} ${rowData.surname}`}
                  </Link>
                ) : (
                  <span>
                    {rowData.companyName
                      ? rowData.contactName
                      : `${rowData.name} ${rowData.surname}`}
                  </span>
                )
              }
            ></Column>
            <Column
              field="companyName"
              header="Company"
              body={(rowData) => <span>{rowData.companyName || "-"}</span>}
            ></Column>
            <Column
              header="Project type"
              body={(rowData) =>
                rowData.contractType?.name || rowData.projectType?.name || "-"
              }
            ></Column>
            <Column field="email" header="Email"></Column>
            <Column field="phone" header="Phone"></Column>
            <Column
              header="Lead owner"
              body={(rowData) => (
                <span>
                  {rowData.creator.firstName} {rowData.creator.lastName}
                </span>
              )}
            ></Column>

            <Column
              field="updatedAt"
              body={UpdatedAt}
              header="Last Edited"
            ></Column>
            {/* <Column
              field="proposalDate"
              body={(rowData) =>
                rowData.proposalDate ? (
                  <span>
                    {new Date(rowData.proposalDate).toLocaleDateString()}
                  </span>
                ) : (
                  <span>-</span>
                )
              }
              header="Proposal date"
            ></Column> */}

            <Column
              field="status"
              body={(rowData: {
                status: LeadStatusEnum;
                id: string;
                sendStatus: boolean;
              }) => <Status {...rowData} />}
              header="Status"
            ></Column>

            <Column
              body={GeocodeStatus}
              header="Geofence"
            ></Column>

            <Column
              field="id"
              body={(rowData: {
                status: LeadStatusEnum;
                id: string;
                sendStatus: boolean;
                pdfPath: string;
                contract: IContract | null;
                isDisabled: boolean;
              }) => <Actions {...rowData} isDisabled={isFetching} />}
              header="Actions"
            ></Column>
          </DataTable>
        </div>
      )}
    </div>
  );
};
